<template>
    <div class="settings-container">
        <!-- Main Content -->
        <div class="main-content">
            <!-- Loading State -->
            <LoadingSpinner v-if="loading" />

            <!-- Settings Content -->
            <div v-else class="settings-section">
                <!-- Settings Header -->
                <SettingsHeader />

                <!-- Settings Cards -->
                <div class="settings-cards">
                    <!-- Appearance Card -->
                    <AppearanceCard
                        :current-theme="preferences.theme"
                        :exam-mode="preferences.exam_mode"
                        @update-theme="updateTheme"
                        @update:examMode="onExamModeChange"
                    />

                    <!-- Notifications Card -->
                    <NotificationsCard
                        :enabled="preferences.notifications_enabled"
                        @update:enabled="onNotificationsChange"
                    />

                    <!-- Learning Experience Card -->
                    <LearningExperienceCard
                        :show-tutorials="preferences.show_tutorials"
                        @update:showTutorials="onTutorialsChange"
                    />

                    <!-- Reset Settings Card -->
                    <ResetSettingsCard
                        @reset-click="showResetConfirm = true"
                    />
                </div>
            </div>
        </div>

        <!-- Reset Confirmation Modal -->
        <ResetConfirmModal
            :show="showResetConfirm"
            @close="showResetConfirm = false"
            @confirm="resetSettings"
        />

        <!-- Toast Notification -->
        <ToastNotification
            :show="showToast"
            :message="toastMessage"
            :type="toastType"
        />

        <!-- Guest Blocking Modal -->
        <GuestBlockingModal
            :show="isGuest"
            featureName="Settings"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";

import LoadingSpinner from "@/components/practice/LoadingSpinner.vue";
import SettingsHeader from "@/components/settings/SettingsHeader.vue";
import AppearanceCard from "@/components/settings/AppearanceCard.vue";
import NotificationsCard from "@/components/settings/NotificationsCard.vue";
import LearningExperienceCard from "@/components/settings/LearningExperienceCard.vue";
import ResetSettingsCard from "@/components/settings/ResetSettingsCard.vue";
import ResetConfirmModal from "@/components/settings/ResetConfirmModal.vue";
import ToastNotification from "@/components/profile/ToastNotification.vue";
import GuestBlockingModal from "@/components/common/GuestBlockingModal.vue";

export default {
    name: "Settings",
    components: {
        LoadingSpinner,
        SettingsHeader,
        AppearanceCard,
        NotificationsCard,
        LearningExperienceCard,
        ResetSettingsCard,
        ResetConfirmModal,
        ToastNotification,
        GuestBlockingModal
    },
    setup() {
        // ==================== SETUP ====================
        const router = useRouter();
        const authStore = useAuthStore();

        // ==================== STATE ====================
        const loading = ref(true);
        const saving = ref(false);
        const showResetConfirm = ref(false);

        const preferences = ref({
            theme: "dark",
            exam_mode: false,
            notifications_enabled: true,
            show_tutorials: true,
        });

        // Toast notification
        const showToast = ref(false);
        const toastMessage = ref("");
        const toastType = ref("success");

        // ==================== COMPUTED ====================
        const user = computed(() => authStore.user);
        const isGuest = computed(() => !authStore.isAuthenticated);

        // ==================== METHODS ====================
        const showToastNotification = (message, type = "success") => {
            toastMessage.value = message;
            toastType.value = type;
            showToast.value = true;
            setTimeout(() => {
                showToast.value = false;
            }, 3000);
        };

        const loadSettings = async () => {
            loading.value = true;
            try {
                const settings = await authStore.fetchSettings();

                preferences.value = {
                    theme: settings.appearance?.theme || "dark",
                    exam_mode: settings.appearance?.exam_mode ?? false,
                    notifications_enabled: settings.notifications?.send_notifications ?? true,
                    show_tutorials: settings.learning?.show_tutorials ?? true,
                };
            } catch (error) {
                console.error("Error loading settings:", error);
                showToastNotification("Failed to load settings", "error");
            } finally {
                loading.value = false;
            }
        };

        const updatePreferences = async () => {
            if (saving.value) return;

            saving.value = true;
            try {
                await authStore.updateSettings({
                    appearance: {
                        theme: preferences.value.theme,
                        exam_mode: preferences.value.exam_mode
                    },
                    notifications: {
                        send_notifications: preferences.value.notifications_enabled
                    },
                    learning: {
                        show_tutorials: preferences.value.show_tutorials
                    }
                });

                showToastNotification('Settings updated');
            } catch (error) {
                console.error('Error updating settings:', error);
                showToastNotification('Failed to update settings', 'error');
            } finally {
                saving.value = false;
            }
        };

        const updateTheme = async (theme) => {
            preferences.value.theme = theme;
            await updatePreferences();
        };

        const onNotificationsChange = async (value) => {
            preferences.value.notifications_enabled = value;
            await updatePreferences();
        };

        const onTutorialsChange = async (value) => {
            preferences.value.show_tutorials = value;
            await updatePreferences();
        };

        const onExamModeChange = async (value) => {
            preferences.value.exam_mode = value;
            await updatePreferences();
        };

        const resetSettings = async () => {
            saving.value = true;
            try {
                const settings = await authStore.resetSettings();

                preferences.value = {
                    theme: settings.appearance?.theme || "dark",
                    exam_mode: settings.appearance?.exam_mode ?? false,
                    notifications_enabled: settings.notifications?.send_notifications ?? true,
                    show_tutorials: settings.learning?.show_tutorials ?? true,
                };

                showResetConfirm.value = false;
                showToastNotification("Settings reset to defaults");
            } catch (error) {
                console.error("Error resetting settings:", error);
                showToastNotification("Failed to reset settings", "error");
            } finally {
                saving.value = false;
            }
        };

        const handleNavigation = (tabId) => {
            router.push(`/${tabId}`);
        };

        const handleLogout = () => {
            authStore.logout();
            router.push("/");
        };

        // ==================== LIFECYCLE ====================
        onMounted(() => {
            // Only load settings for authenticated users
            if (authStore.isAuthenticated) {
                loadSettings();
            } else {
                loading.value = false;
            }
        });

        // ==================== RETURN ====================
        return {
            loading,
            saving,
            user,
            isGuest,
            preferences,
            showResetConfirm,
            showToast,
            toastMessage,
            toastType,
            updateTheme,
            onNotificationsChange,
            onTutorialsChange,
            onExamModeChange,
            resetSettings,
            handleNavigation,
            handleLogout,
        };
    },
};
</script>

<style scoped>
* {
    font-family: "Space Grotesk";
    font-weight: 400;
    letter-spacing: 1px;
}

::-webkit-scrollbar {
    width: 20px;
}

::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 10px;
}

.settings-container {
    min-height: 100vh;
    background: var(--bg-primary);
    justify-content: center;
    display: flex;
}

.main-content {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
}

@media screen and (max-width: 1400px) {
    .main-content {
        padding: 0;
        margin: 0;
    }
}

@media screen and (max-width: 1200px) {
    .main-content {
        max-width: 100%;
        padding: 0;
        margin: 0;
    }
    .settings-section {
        padding: 70px 15%;
    }
}

@media screen and (max-width: 768px) {
    .main-content {
        padding: 0;
        margin: auto;
    }
}

.settings-section {
    background: var(--bg-primary);
    padding: 70px 10%;
    margin: 0 auto;
}

/* Settings Cards */
.settings-cards {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .settings-section {
        padding: 20px;
    }
}
</style>
